<template>
  <div>
    <DxForm id="formularioFactura" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" validation-group="validacionFactura">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="Fecha" editor-type="dxDateBox" :col-span="2">
          <DxLabel :text="$t('inmueble.factura.fecha')" />
          <DxRequiredRule :message="$t('inmueble.validations.fecha')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="TipoSuministro" editor-type="dxSelectBox" :col-span="2" 
          :editor-options="{...getDomainEnumSelectBox('tipoFacturaInmueble', 'values')}">
          <DxLabel :text="$t('inmueble.factura.tipo')" />
          <DxRequiredRule :message="$t('inmueble.validations.tipoFactura')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Importe" editor-type="dxNumberBox" :col-span="2" :editor-options="importeNumberOptions">
          <DxLabel :text="$t('inmueble.factura.importe')" />
          <DxRequiredRule :message="$t('inmueble.validations.importe')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="6">
          <DocumentoCard ref="documentoCard" :title="$t('inmueble.factura.ui.factura')"
            :required="true" align="horizontal" layout="default"/>
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxSimpleItem,
  DxGroupItem
} from "devextreme-vue/form";
import { getCurrentInstance, onMounted, reactive, ref } from 'vue';
import { useIntl } from '@services/i18n';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import type { AddFacturaInmueble } from '@/components/inmuebles/models/AddFacturaInmueble';
import { getDomainEnumSelectBox } from '@/.shared/helpers/grid-helper';
import DocumentoCard from '@/.shared/components/document-card/DocumentCard.vue';
import type { CardFile } from '@/.shared/components/document-card/models/CardFile';

const api = useApi();
const formInstance = ref<DxForm>();
const documentoASubir = ref<File>();
const documentoCard = ref<InstanceType<typeof DocumentoCard>>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => AddFacturaInmueble,
    required: false
  },
  inmuebleId: {
    type: String,
    required: true
  }
});

const importeNumberOptions = {
  showSpinButtons: true,
  step: 0.1,
  format: {
    type: "fixedPoint",
    precision: 2,
    formatter: (value: number) => {
      return intl.i18n.n(Number(value.toFixed(2)), 'decimal') + ' €';
    }
  }
};

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionFactura");
  var res2 = documentoCard.value?.isValid ?? false;
  const files = documentoCard.value?.getFiles();
  const cardFile = files ? Object.values(files)[0] : undefined;

  if (res.isValid && res2 && cardFile) {
    await api.callStandardApi("postDocumentoTemp", {showLoading: false}, cardFile.fileName, cardFile.file!)
      .then(async (tempUrl: string) => {
        props.model?.setRutaTemporal(tempUrl);
        await api.callStandardApi("addFacturaInmueble", {showLoading: true}, props.inmuebleId, props.model )
          .then(() => toastSuccess(intl.i18n.t('inmueble.factura.ui.creacion_exito')))
          .catch(() => {
            toastError(intl.i18n.t('inmueble.factura.ui.creacion_fallo'));
            return false;
          });
      })
      .catch(() => {
        toastError(intl.i18n.t('inmueble.factura.ui.creacion_fallo'));
        return false;
      });
    return true;
  } else {
    toastError(intl.i18n.t('inmueble.factura.ui.revise_validacion'));
  }
}

</script>